import React, {
  ReactNode,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import {
  Card,
  Dropdown,
  Row,
  Select,
  Space,
  Drawer,
  Table,
  Popover,
  Popconfirm,
  Col,
  Typography,
} from "antd";
import type { TableColumnsType, TableProps } from "antd";
import { Button, Form, Input, Radio } from "antd";
import { ReloadOutlined, PlusOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  datatableListPostApiFunction,
  datatablelistApiFunction,
} from "./slice-datatable";
import { ColumnsType } from "antd/es/table";

type UrlBuilder = (pageNo: any, pageSize: any) => void;

type onPageCallback = () => void;

const TablePaginationConfig = ({
  onRowSlection,
  components,
  tableStyleClass,
  selectedRows,
  pagination,
  data,
  onRow,
  loading,
  rowSelection,
  key,
  columns,
  rowClassName,
  bordered,
  children,
}: any) => {
  return (
    <div
      className={
        tableStyleClass ? tableStyleClass + " dataTableMobileView" : ""
      }
    >
      {" "}
      <Table
        rowSelection={onRowSlection && selectedRows ? rowSelection : false}
        dataSource={data}
        rowKey={key}
        loading={loading}
        components={components}
        onRow={onRow}
        rowClassName={rowClassName}
        bordered={bordered}
        pagination={pagination}
        columns={columns}
      >
        {children}
      </Table>
    </div>
  );
};

export const DataTable = forwardRef(
  (
    {
      datatransform,
      extra,
      children,
      tableData,
      key = "id",
      title,
      buildApiUrl,
      onRow,
      defaultPageSize = 10,
      componentuniquekey,
      columns,
      lazyPagination = false,
      onPaginationChange,
      buildBody = () => {
        return undefined;
      },
      method = "GET",
      tableStyleClass,
      cardView = true,
      components,
      rowClassName,
      bordered,
      disablePagination = false,
      selectedRows,
      onRowSlection,
      checkboxProps,
    }: {
      datatransform?: any;
      columns?: ColumnsType<any>;
      children?: any;
      extra?: ReactNode;
      title?: any;
      key: string;
      defaultPageSize?: number;
      tableStyleClass?: string;
      disablePagination?:boolean,
      buildBody?: any;
      onRow?: any;
      tableData?: any[];
      onPaginationChange?: onPageCallback;
      components?: any;
      bordered?: boolean;
      rowClassName?: string | any;
      selectedRows?: string[];
      onRowSlection?: any;
      checkboxProps?: any;
      method?: "GET" | "POST";
      onRefresh?: any;
      cardView?: boolean;
      buildApiUrl: UrlBuilder;
      lazyPagination?: boolean;
      componentuniquekey: string;
    },
    ref
  ) => {
    const datatabledata = useSelector((state: any) => state.datatable);
    let { data, loading } = (datatabledata || {})[componentuniquekey] || {};
    const { content, totalElements, numberOfElements } = data || {
      content: [],
      totalElements: 0,
    };

    const [page, setPage] = useState({
      pageNo: 1,
      pageSize: defaultPageSize,
      total: 0,
    });
    const apiFunction =
      method === "GET"
        ? datatablelistApiFunction
        : datatableListPostApiFunction;

    const refreshPage = () => {
      if (!tableData) {
        dispatch(
          apiFunction({
            componentuniquekey,
            url: buildApiUrl(page.pageNo, page.pageSize),
            method,
            body: buildBody(),
          })
        );
      }
    };
    const dispatch = useDispatch();
    useEffect(() => {
      refreshPage();
    }, []);

    const onPageChange = (pageNo: number) => {
      setPage({ ...page, pageNo: pageNo });
      if (lazyPagination) {
        dispatch(
          apiFunction({
            componentuniquekey,
            url: buildApiUrl(pageNo, page.pageSize),
            method,
            body: buildBody(),
          })
        );
      }
      if (onPaginationChange) {
        onPaginationChange();
      }
    };

    const onPageSizeChange = (pageSize: number) => {
      setPage({ ...page, pageSize: pageSize, pageNo: 1 });
      if (lazyPagination) {
        dispatch(
          apiFunction({
            componentuniquekey,
            url: buildApiUrl(1, pageSize),
            method,
            body: buildBody(),
          })
        );
      }
      if (onPaginationChange) {
        onPaginationChange();
      }
    };

    useImperativeHandle(ref, () => ({
      refresh: () => {
        refreshPage();
      },
    }));

    const rowSelection = {
      type: "checkbox",
      selectedRowKeys: selectedRows,
      onSelect: (selectedRowKeys: any) => {
        let ss = [];
        if (selectedRows && selectedRows.indexOf(selectedRowKeys.id) < 0) {
          ss = [...selectedRows, selectedRowKeys.id];
        } else {
          const n = selectedRows ? [...selectedRows] : [];
          const index = n.indexOf(selectedRowKeys.id);
          if (index > -1) {
            n.splice(index, 1);
          }
          ss = n;
        }
        onRowSlection(ss);
      },
      onSelectNone: () => {
        onRowSlection([]);
      },
      onSelectAll: (selectedRowKeys: any, data: any) => {
        if (selectedRowKeys) {
          onRowSlection(data.map((e: any) => e[key]));
        } else {
          onRowSlection([]);
        }
      },
      getCheckboxProps: (record: any) => ({
        name: record.id,
        key: record.id,
        ...(checkboxProps ? checkboxProps(record) || {} : {}),
      }),
    } as any;

    if (tableData) {
      lazyPagination = false;
      data = tableData;
    }

    const finalTableData = tableData
      ? tableData
      : datatransform
      ? datatransform((lazyPagination ? content : data) || [])
      : (lazyPagination ? content : data) || [];
    const finalTotalRecords = lazyPagination
      ? totalElements
      : (data || []).length;
    const pagination = disablePagination?false: {
      current: page.pageNo,
      pageSize: page.pageSize,
      total: finalTotalRecords,
      pageSizeOptions: [],
      showSizeChanger: false,
      onChange: onPageChange,
    };

    return (
      <>
        <div className="datatable-div">
          {cardView ? (
            <Card
              title={title}
              loading={loading}
              extra={
                <Space>
                  {extra}
                  {tableData ? null : (
                    <>
                      <Button loading={loading} onClick={() => refreshPage()}>
                        {loading ? null : <ReloadOutlined />}
                        Refresh{" "}
                      </Button>
                      <Select
                        defaultValue={10}
                        style={{ width: 120 }}
                        onChange={onPageSizeChange}
                        disabled={loading}
                        options={[
                          { value: 10, label: "10 / page" },
                          { value: 20, label: "20 / page" },
                          { value: 50, label: "50 / page" },
                          { value: 100, label: "100 / page" },
                        ]}
                      />
                    </>
                  )}
                </Space>
              }
            >
              <TablePaginationConfig
                onRow={onRow}
                tableStyleClass={tableStyleClass}
                onRowSlection={onRowSlection}
                components={components}
                selectedRows={selectedRows}
                page={page}
                loading={loading}
                pagination={pagination}
                data={finalTableData}
                rowSelection={rowSelection}
                key={key}
                columns={columns}
                rowClassName={rowClassName}
                bordered={bordered}
                onPageChange={onPageChange}
              >
                {children}
              </TablePaginationConfig>
            </Card>
          ) : (
            <>
              {tableData ? null : (
                <Row>
                  <Col span={12}>
                    <Typography.Text type="secondary" className="d-block mb-2">
                      Showing{" "}
                      {(page.pageNo - 1) * page.pageSize +
                        (finalTableData.length > 0 ? 1 : 0)}{" "}
                      to{" "}
                      {(page.pageNo - 1) * page.pageSize +
                        (lazyPagination
                          ? numberOfElements
                          : (data || []).length)}{" "}
                      of {lazyPagination ? totalElements : (data || []).length} {" "}
                      records
                    </Typography.Text>
                  </Col>
                  <Col span={12} style={{ textAlign: "right" }}>
                    <Select
                      className="filterTextfieldBox"
                      defaultValue={10}
                      style={{ width: 120 }}
                      onChange={onPageSizeChange}
                      // disabled={loading}
                      options={[
                        { value: 10, label: "10 / page" },
                        { value: 20, label: "20 / page" },
                        { value: 50, label: "50 / page" },
                        { value: 100, label: "100 / page" },
                      ]}
                    />
                  </Col>
                </Row>
              )}
              <TablePaginationConfig
                onRow={onRow}
                tableStyleClass={tableStyleClass}
                onRowSlection={onRowSlection}
                components={components}
                selectedRows={selectedRows}
                page={page}
                loading={loading}
                pagination={pagination}
                data={finalTableData}
                rowSelection={rowSelection}
                key={key}
                columns={columns}
                rowClassName={rowClassName}
                bordered={bordered}
                onPageChange={onPageChange}
              >
                {children}
              </TablePaginationConfig>
            </>
          )}
        </div>
      </>
    );
  }
);
